import { useAnalytics as praxisAnalytics } from '@praxis/analytics/client'
import { useEffect, useRef } from 'react'
import { FireflyEvent } from './fireflyEvent'
import { useAuth } from '@praxis/component-auth'
import { safeTgtLogger } from './safeTgtLogger'
import { withUserInfo } from './analyticsUserInfo'

const isFireflyEvent = (event: any): event is FireflyEvent =>
    typeof event === 'object' && !!event?.event?.type

const getLanIdUpper = (session: any) =>
    (session?.userInfo?.lanId || 'unknown').toUpperCase()

// Safe wrapper for useAnalytics that handles cases where AnalyticsProvider isn't available.
// Every Firefly event goes through here, so the MPM `user` object is attached in one place.
export const useSafeAnalytics = () => {
    try {
        const analytics = praxisAnalytics()

        return {
            sendClickEvent:
                typeof analytics?.sendClickEvent === 'function'
                    ? (eventType: string, payload: any) => {
                          try {
                              return analytics.sendClickEvent(
                                  eventType,
                                  withUserInfo(payload),
                              )
                          } catch {
                              return undefined
                          }
                      }
                    : () => {
                          return undefined
                      },

            sendEvent:
                typeof analytics?.sendEvent === 'function'
                    ? (payload: any) => {
                          try {
                              return analytics.sendEvent(withUserInfo(payload))
                          } catch {
                              return undefined
                          }
                      }
                    : () => {
                          return undefined
                      },
        }
    } catch {
        return {
            sendClickEvent: () => undefined,
            sendEvent: () => undefined,
        }
    }
}

export const useLogClick = (viewName: string) => {
    const analytics = useSafeAnalytics()
    const { session } = useAuth()

    const logClick = (eventValue: string, eventId?: string) => {
        try {
            const payload = {
                client: {
                    page_name: viewName,
                    employee_login_id: getLanIdUpper(session),
                    version: process.env.APP_VERSION || '0.0.0',
                },
                event: {
                    value: eventValue,
                    id: eventId,
                },
            }

            const result = analytics.sendClickEvent('CLICK', payload)

            if (result === undefined) {
                safeTgtLogger.warn(
                    `Analytics not available - click event "${eventValue}" not sent for page "${viewName}"`,
                    {
                        event: {
                            action: 'analytics_click_event_skipped',
                            category: 'analytics',
                            outcome: 'failure',
                        },
                        nonIndexedFields: { eventName: eventValue, viewName },
                    },
                )
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Unknown error'
            safeTgtLogger.error(
                `Failed to log click event "${eventValue}" for page "${viewName}": ${errorMessage}`,
                {
                    error: {
                        message: errorMessage,
                        type: error instanceof Error ? error.name : 'Error',
                        stack_trace:
                            error instanceof Error && error.stack
                                ? error.stack
                                : 'No stack trace available',
                    },
                    event: {
                        action: 'analytics_click_event_failed',
                        category: 'analytics',
                        outcome: 'failure',
                    },
                    nonIndexedFields: { eventName: eventValue, viewName },
                },
            )
        }
    }

    return { logClick }
}

export const usePageView = (pageName: string) => {
    const analytics = useSafeAnalytics()
    const { session } = useAuth()

    const trackPageView = () => {
        const payload = {
            event: {
                type: 'PAGE_VIEW' as any,
                value: pageName,
            },
            client: {
                page_name: pageName,
                employee_login_id: getLanIdUpper(session),
                version: process.env.APP_VERSION || '0.0.0',
            },
        }

        void analytics.sendEvent(payload)
    }

    return { trackPageView }
}

// Auto-track page views on route changes
export const useAutoPageTracking = () => {
    const analytics = useSafeAnalytics()
    const { session } = useAuth()

    useEffect(() => {
        const trackCurrentPage = () => {
            const pathname = window.location.pathname
            const pageName = pathname === '/' ? 'home' : pathname

            const payload = {
                event: {
                    type: 'PAGE_VIEW' as any,
                    value: pageName,
                },
                client: {
                    page_name: pageName,
                    employee_login_id: getLanIdUpper(session),
                    version: process.env.APP_VERSION || '0.0.0',
                    page_url: pathname,
                },
            }

            void analytics.sendEvent(payload)
        }

        // Track the current page on mount
        trackCurrentPage()

        // Listen for browser navigation events (back/forward)
        const onPopState = () => trackCurrentPage()
        window.addEventListener('popstate', onPopState)

        return () => window.removeEventListener('popstate', onPopState)
    }, [analytics, session])
}

export const useDwellTime = (pageName: string) => {
    const analytics = useSafeAnalytics()
    const { session } = useAuth()
    const activeTimeRef = useRef(0)
    const startTimeRef = useRef<number | null>(Date.now())
    const analyticsRef = useRef(analytics)
    const sessionRef = useRef(session)
    // Keep refs in sync without triggering the effect
    analyticsRef.current = analytics
    sessionRef.current = session

    useEffect(() => {
        const flush = () => {
            if (startTimeRef.current !== null) {
                activeTimeRef.current += Date.now() - startTimeRef.current
                startTimeRef.current = null
            }

            const duration_ms = activeTimeRef.current
            if (duration_ms < 1000) return

            activeTimeRef.current = 0

            void analyticsRef.current.sendEvent({
                event: {
                    type: 'DWELL_TIME' as any,
                    value: duration_ms.toString(),
                },
                client: {
                    page_name: pageName,
                    employee_login_id: getLanIdUpper(sessionRef.current),
                    version: process.env.APP_VERSION || '0.0.0',
                },
            })
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                if (startTimeRef.current !== null) {
                    activeTimeRef.current += Date.now() - startTimeRef.current
                    startTimeRef.current = null
                }
            } else {
                startTimeRef.current = Date.now()
            }
        }

        document.addEventListener('visibilitychange', onVisibilityChange)
        window.addEventListener('pagehide', flush)

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange)
            window.removeEventListener('pagehide', flush)
            flush()
        }
    }, [pageName])
}

export const useAnalytics = () => {
    const analytics = useSafeAnalytics()
    const { session } = useAuth()
    const trackEvent = (
        eventTypeOrFireflyEvent: string | FireflyEvent,
        eventData?: any,
    ) => {
        if (isFireflyEvent(eventTypeOrFireflyEvent)) {
            const fireflyEvent = eventTypeOrFireflyEvent

            const mappedData = {
                event: {
                    type: 'SERVICE_CALL' as any,
                    value:
                        (fireflyEvent.view?.additionalInfo as any)
                            ?.request_url || 'service_call',
                },
                client: {
                    employee_login_id: getLanIdUpper(session),
                    version: process.env.APP_VERSION || '0.0.0',
                },
                ...(fireflyEvent.error?.id && {
                    interface_message: {
                        error_code: fireflyEvent.error.id,
                    },
                }),
            }
            ;(analytics.sendEvent as any)(mappedData)
        }
        const eventType = eventTypeOrFireflyEvent as string
        if (eventType === 'SERVICE_CALL') {
            const mappedData = {
                event: {
                    type: 'SERVICE_CALL' as any,
                    value:
                        eventData?.client.page_url ||
                        eventData?.url ||
                        'service_call',
                },
                client: {
                    employee_login_id: getLanIdUpper(session),
                    version: process.env.APP_VERSION || '0.0.0',
                    page_url: eventData?.page_url,
                },
                ...(eventData?.error && {
                    interface_message: {
                        error_code:
                            eventData.error.id ||
                            eventData.error.code ||
                            'unknown_error',
                    },
                }),
            }

            ;(analytics.sendEvent as any)(mappedData)
        }
    }

    return {
        trackEvent,
        sendEvent: analytics.sendEvent,
    }
}
